/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { useAsyncDebounce } from "react-table";
import { Alert, Button, Card, ControlledTable, Input, Select, SvgIcon, Table } from "core/components";
import { admin_material_reordering_index_path } from "routes";
import { api, ApiError } from "core/api";
import { useForm } from "react-hook-form";
import { MaterialReorderingSchema } from "core/schema";
import { yupResolver } from "@hookform/resolvers/yup";
import { navigateTo } from "core/navigation";
import ReplenishmentRequests from "./ReplenishmentRequests";
import moment from "moment";
import ControlledTableContext from "core/components/ControlledTableContext";

const MaterialReordering = ({ warehouses, statuses, has_item_inventories, has_purchase_requests }): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [itemInventories, setItemInventories] = React.useState([]);
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = React.useState(false);
  const [item, setItem] = React.useState(null);
  const [purchaseRequestCode, setPurchaseRequestCode] = React.useState("");
  const [showPurchaseRequestsModal, setShowPurchaseRequestsModal] = React.useState(false);
  const [purchaseRequests, setPurchaseRequests] = React.useState([]);
  const [showPurchaseOrdersModal, setShowPurchaseOrdersModal] = React.useState(false);
  const [purchaseOrders, setPurchaseOrders] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { isSubmitting, isValidating }
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(MaterialReorderingSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [warehouseFilter, setWarehouseFilter] = React.useState("");
  const table = React.useRef(null);
  const abortController = React.useRef<AbortController>();

  const addToSelectedItems = (item) => {
    if (selectedItems.find((selectedItem) => selectedItem?.id == item?.id)) {
      return;
    } else {
      setSelectedItems((items) => [...items, item]);
    }
  };

  const removeFromSelectedItems = (item) => {
    setSelectedItems((selectedItems) => selectedItems.filter((selectedItem) => selectedItem.id != item.id));
  };

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        Header: `Select`,
        accessor: "selected",
        style: {
          width: "5%"
        },
        Cell: (props) => {
          if (props.row.original.purchase_request_items?.length > 0 && props.row.original.is_above_reorder_point) return "";

          const { item } = props.row.original;
          const availableQuantity = parseFloat(props.row.original.available_quantity);
          const safetyStockLevel = parseFloat(props.row.original.safety_stock_level);
          const pendingPurchasesQuantity = parseFloat(props.row.original.pending_purchases_quantity);
          const requestedQuantity = safetyStockLevel - (availableQuantity + pendingPurchasesQuantity);
          setValue(`material_reordering.${props.row.index}.item_id`, props.row.original.id);
          setValue(`material_reordering.${props.row.index}.requested_quantity`, requestedQuantity);
          setValue(`material_reordering.${props.row.index}.item`, props.row.original.item);

          return (
            <div className="d-flex justify-content-center">
              <label className="checkbox-label">
                <input
                  {...register(`material_reordering.${props.row.index}.selected`)}
                  className="field--checkbox"
                  type="checkbox"
                  defaultChecked={props.selectedItems.some((item) => item.id === props.row.original.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      addToSelectedItems({ ...item, item_id: item.id, requested_quantity: requestedQuantity });
                    } else {
                      removeFromSelectedItems(item);
                    }
                  }}
                />
                <div className="checkbox">
                  <SvgIcon name="check" />
                </div>
              </label>
            </div>
          );
        }
      },
      {
        id: "item_code",
        Header: "Inventory ID",
        accessor: "item.code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        id: "item_name",
        Header: "Inventory",
        accessor: "item.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure_code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Warehouse",
        accessor: "warehouse.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Available Quantity",
        accessor: "available_quantity",
        Cell: (props) => {
          return props.value ? (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          ) : (
            <p className="text-center">{"0.0"}</p>
          );
        }
      },
      {
        Header: "Reorder Point",
        accessor: "reorder_point",
        Cell: (props) => {
          return props.value ? (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          ) : (
            <p className="text-center">{"0.0"}</p>
          );
        }
      },
      {
        Header: "Safety Stock Level",
        accessor: "safety_stock_level",
        Cell: (props) => {
          return props.value ? (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          ) : (
            <p className="text-center">{"0.0"}</p>
          );
        }
      },
      {
        Header: "View P.R",
        accessor: "",
        Cell: (props) => {
          const purchaseRequestItems = props.row.original.purchase_request_items;
          const hasPurchaseRequestItems = purchaseRequestItems?.length > 0;

          if (!hasPurchaseRequestItems) return <></>;

          return (
            <div className="d-flex justify-content-center">
              <button
                className="button button--action button--action-success"
                title={"View Purchase Requests"}
                data-bs-toggle="tooltip"
                onClick={() => {
                  setItem(props.row.original.item);
                  setShowPurchaseRequestsModal(!showPurchaseRequestsModal);
                  setPurchaseRequests(purchaseRequestItems);
                }}
              >
                <SvgIcon name="clipboard-list" className="h5" />
              </button>
            </div>
          );
        }
      }
    ],
    []
  );

  const purchaseRequestColumns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: () => {
          return <></>;
        }
      },
      {
        Header: "Purchase Request",
        accessor: "purchase_request_code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Requested Quantity",
        accessor: "requested_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Ordered Quantity",
        accessor: "ordered_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Delivered Quantity",
        accessor: "delivered_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.purchase_request.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      },
      {
        Header: "View P.O",
        accessor: "",
        Cell: (props) => {
          const purchaseOrders = props.row.original.purchase_orders;
          const hasPurchaseOrders = purchaseOrders?.length > 0;

          if (!hasPurchaseOrders) return <></>;

          return (
            <div className="d-flex justify-content-center">
              <button
                className="button button--action button--action-success"
                title={"View Purchase Orders"}
                data-bs-toggle="tooltip"
                onClick={() => {
                  setPurchaseRequestCode(props.row.original.purchase_request_code);
                  setShowPurchaseOrdersModal(!showPurchaseOrdersModal);
                  setPurchaseOrders(purchaseOrders);
                }}
              >
                <SvgIcon name="clipboard-list" className="h5" />
              </button>
            </div>
          );
        }
      }
    ],
    []
  );

  const purchaseOrderColumns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: () => {
          return <></>;
        }
      },
      {
        Header: "Purchase Order",
        accessor: "code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Supplier",
        accessor: "supplier.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Ordered Quantity",
        accessor: "ordered_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Expected Delivery Date",
        accessor: "expected_delivery_date",
        Cell: (props) => {
          if (!props.value) return <p className="text-center">-</p>;

          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{moment(props.value).format("MM/DD/YYYY")}</p>
            </div>
          );
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.purchase_order.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      }
    ],
    []
  );

  const getItemInventoriesForReordering = React.useCallback(
    async ({ warehouseId = null, page, per, filters }) => {
      const warehouse_id = warehouseId || warehouseFilter;

      try {
        setIsLoading(true);
        // If there is a pending fetch request with associated AbortController, abort
        if (abortController.current) abortController.current.abort();
        // Assign a new AbortController for the latest fetch to our useRef variable
        abortController.current = new AbortController();
        const { signal } = abortController.current;

        let response = null;
        if (warehouse_id) {
          response = await api.get(admin_material_reordering_index_path({ warehouse_id, page, per, filters: JSON.stringify(filters) }), { signal });
        } else {
          response = await api.get(admin_material_reordering_index_path({ page, per, filters: JSON.stringify(filters) }), { signal });
        }
        setItemInventories(
          response.data.data.map((d, i) => {
            setValue(
              `material_reordering.${i}.selected`,
              selectedItems.some((item) => item.id === d.id)
            );
            return {
              ...d,
              warehouse: d.warehouse
            };
          })
        );

        if (response.data.data.length > 0) {
          setValue("warehouse_id", response.data.data[0].warehouse.id);
        }
        setPagination(response.data.pagination);
        setCurrentPage(response.data.pagination?.current || currentPage);
        return { pagination: response.data.pagination, data: response.data.data };
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [warehouseFilter]
  );

  const save = async (data): Promise<unknown> => {
    try {
      await api.post(admin_material_reordering_index_path(), {
        warehouse_id: data.warehouse_id,
        material_reordering: selectedItems
      });
      navigateTo(admin_material_reordering_index_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const onError = (data) => {
    console.error(data);
    if (data?.material_reordering) {
      setFormErrors([data.material_reordering.message]);
    } else {
      setFormErrors(["There are errors on your form."]);
    }
  };

  const onChange = useAsyncDebounce((column, value) => {
    table.current.setFilter(column, value);
  }, 400);

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="inventory" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No item inventories for replenishment.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Inventory ID" onChange={(e) => onChange("item_code", e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Inventory Name" onChange={(e) => onChange("item_name", e.target.value)} />
        </Col>
        <Col>
          <Select
            placeholder="Filter by Warehouse"
            isClearable={false}
            options={warehouses}
            isSearchable={false}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            isOptionDisabled={(option) => !option.active}
            onChange={(newOption) => {
              setWarehouseFilter(newOption.id);
              setSelectedItems([]);
            }}
            value={warehouses.find((warehouse) => [warehouseFilter, itemInventories[0]?.warehouse?.id].includes(warehouse.id)) || ""}
          />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
            <ControlledTable
              columns={columns}
              data={itemInventories}
              ref={table}
              isLoading={isLoading}
              fetchData={getItemInventoriesForReordering}
              totalCount={pagination?.total_count}
              totalPageCount={pagination?.total_pages || 0}
              previous={pagination?.previous}
              next={pagination?.next}
              selectedItems={selectedItems}
            />
          </ControlledTableContext.Provider>
        </Col>
      </Row>
    );
  };

  const content = () => {
    return has_item_inventories > 0 ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  return (
    <React.StrictMode>
      {formErrors && formErrors.length != 0 && (
        <Alert>
          {formErrors.map((error, index) => {
            return <div key={index}>{error}</div>;
          })}
        </Alert>
      )}
      <Tabs className="card" selectedTabClassName="project-tab__link project-tab__link--active">
        <Container fluid={true}>
          <Row className="d-flex align-items-baseline">
            <Col>
              <TabList className="card__header" style={{ padding: 0 }}>
                <Col className="d-flex justify-content-start">
                  <Tab className="project-tab__link">Material Replenishments</Tab>
                  <Tab className="project-tab__link">Replenishment Requests</Tab>
                </Col>
              </TabList>
            </Col>
          </Row>
          <TabPanel>
            <Card.Header title="">
              {itemInventories.length > 0 && (
                <Button
                  className="button--brand mr-3"
                  loading={isSubmitting || isValidating}
                  disabled={isSubmitting || isValidating}
                  icon="clipboard-list"
                  title="Generate Replenishment Request"
                  onClick={async () => {
                    const result = await trigger(undefined, { shouldFocus: true });

                    if (result) {
                      setShowConfirmationModal(!showConfirmationModal);
                    } else {
                      handleSubmit(async (data) => await save(data), onError)();
                    }
                  }}
                />
              )}
            </Card.Header>
            <Card.Body>{content()}</Card.Body>
          </TabPanel>
          <TabPanel>
            <ReplenishmentRequests warehouses={warehouses} statuses={statuses} hasPurchaseRequests={has_purchase_requests} />
          </TabPanel>
        </Container>
      </Tabs>
      <Modal dialogClassName="modal-dialog-centered" show={showConfirmationModal}>
        <Modal.Header>Generate Replenishment Request</Modal.Header>
        <Modal.Body>
          <div className="mb-2">Are you sure you want to generate these requests?</div>
          {selectedItems.map((item) => {
            return (
              <div key={item.id}>
                <span className="text-bold">{item.code}</span>
                <span>{` - ${item.name}`}</span>
              </div>
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowConfirmationModal(!showConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--brand"
            icon="clipboard-list"
            title="Yes, generate requests."
            loading={isSubmitting || isValidating}
            disabled={isSubmitting || isValidating}
            onClick={() => {
              setShowConfirmationModal(!showConfirmationModal);
              handleSubmit(async (data) => await save(data), onError)();
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered modal-lg" show={showPurchaseRequestsModal}>
        <Modal.Header>{`View Purchase Requests for: ${item?.code} - ${item?.name}`}</Modal.Header>
        <Modal.Body>
          {purchaseRequests.length > 0 ? (
            <>
              <Table columns={purchaseRequestColumns} data={purchaseRequests} />
            </>
          ) : (
            <div className="text-center mt-4 mb-4">No available purchase requests.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowPurchaseRequestsModal(!showPurchaseRequestsModal);
            }}
            icon="times"
            title="Close"
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered modal-lg" show={showPurchaseOrdersModal}>
        <Modal.Header>{`View Purchase Orders for: ${purchaseRequestCode}`}</Modal.Header>
        <Modal.Body>
          {purchaseOrders.length > 0 ? (
            <>
              <Table columns={purchaseOrderColumns} data={purchaseOrders} />
            </>
          ) : (
            <div className="text-center mt-4 mb-4">No available purchase orders.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowPurchaseOrdersModal(!showPurchaseOrdersModal);
            }}
            icon="times"
            title="Close"
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default MaterialReordering;
